'use no memo';

/* eslint-disable complexity, react/jsx-no-useless-fragment */
import React, { useEffect, useState, useContext } from 'react';
import cx from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { get } from 'lodash-es';
import Cookies from 'universal-cookie';
import { useParams } from 'react-router';

import 'normalize.css';
import '@mantine/dates/styles.layer.css';

import { isLocalStorageAllowed, isSessionStorageAllowed } from '@local/utils';
import { useRestaurantQuery } from '@local/api';

import { isIE } from 'helpers/url';
import { usePersistExperience } from 'hooks/usePersistExperience';
import { hasApplePayCard } from 'helpers/payments';
import { isYelpMobile, handleYelpMessage } from 'helpers/yelp';

import usePlatform from 'hooks/usePlatform';

import { UserContext } from 'context/UserContext';
import { ThemeContext } from 'context/ThemeContext';

import ErrorBoundary from 'components/higher-order/ErrorBoundary';
import { BrowserUnsupportedPage } from 'pages';
import SkipLink from 'components/SkipLink';

import './App.css';
import styles from './App.styles.module.scss';
import AppRoutes from './AppRoutes.tsx';

const TEST_COOKIE = 'cn_test_cookie';

const cookies = new Cookies();
let parentWindow;
const IFRAME_EMBED_RECHECK_INTERVAL = 10;
const MAX_CHECK_IFRAME_EMBED_CHECKS = 10_000 / IFRAME_EMBED_RECHECK_INTERVAL; // 10s in 10ms chunks

export function getCanServeEmbeddedSite() {
  /**
   * Verify a cookie can actually be set and it's value retrieved. Sometimes cookies can be enabled in the
   * browser but disabled in preferences from "3rd party websites", "Cross-Site Cookies",
   * or sites the user has not visited, which can prevent authentication and some other features
   * in our app from working as expected when embedded on another site. CN-1927, CN-6855, CN-9327, etc...
   *
   * Additionally, we are only allowing users to order if sessionStorage is enabled as well CN-11377
   */

  // Set a cookie and ensure it was persisted. This will fail on Safari (and possibly other browsers)
  // as this JS is pulled from an outside domain (ours) and we are attempting to set a client side cookie
  // in the restaurant's environment.

  // https://chromestatus.com/feature/5088147346030592
  // Test with domain '.chownow.com' to match the actual experiment cookie behavior
  // This ensures the test accurately reflects whether LaunchDarkly cookies will work
  cookies.set(TEST_COOKIE, true, {
    secure: true,
    sameSite: 'none',
    domain: '.chownow.com',
  });
  const testCookie = cookies.get(TEST_COOKIE);

  if (isSessionStorageAllowed() && testCookie) {
    // Remove the cookie if we were able to set it.
    cookies.remove(TEST_COOKIE, { domain: '.chownow.com' });
    return true;
  }

  return false;
}

function App() {
  // save experience type (order, dine-in, or catering) to session storage
  usePersistExperience();

  const { isDirectPlatform, isMarketplacePlatform, isYelpPlatform } =
    usePlatform();
  const { theme } = useContext(ThemeContext);

  const { restaurantId } = useParams();
  const { data: restaurant } = useRestaurantQuery(restaurantId);

  useEffect(() => {
    const setCurrencyCode = window.mParticle?.eCommerce?.setCurrencyCode;
    if (setCurrencyCode && restaurant?.currency)
      setCurrencyCode(restaurant.currency);
  }, [restaurant?.currency]);

  // apply the theme class to <html> so CSS custom properties from `_application.scss`
  // are inherited by portaled content (mantine modals, popovers, etc.) that mounts
  // outside the react tree
  React.useLayoutEffect(() => {
    const root = document.documentElement;
    const previous = Array.from(root.classList).filter((c) =>
      c.startsWith('theme-')
    );
    root.classList.remove(...previous);
    root.classList.add(`theme-${theme}`);
  }, [theme]);

  const [hasActiveApplePayCard, setHasActiveApplePayCard] = useState(false);
  const [isApplePayLoading, setIsApplePayLoading] = useState(true);

  const { setSessionId, handleFetchUserProfile } = useContext(UserContext);

  function checkIfCnEmbed(evt) {
    let data;

    // Try to parse message, run in try / catch since could come from anywhere or be malformed
    try {
      data = JSON.parse(evt.data);
    } catch (e) {
      // Do nothing
    }

    // Validating messages from our sources only and ignoring others(e.g. Facebook)
    if (
      get(data, 'source') === 'chownow.ordering' &&
      get(data, 'type') === 'init'
    ) {
      // save parent iframe reference
      parentWindow = evt.source;
    }
  }

  function onEmbeddedAppLoad() {
    const message = JSON.stringify({
      eventName: 'chownow.ordering.onload',
    });

    let count = 0;
    // poll for max 10s

    // Check if parent window has been set, if not check rapidly until we receive event
    function checkParentWindow() {
      if (parentWindow) {
        parentWindow.postMessage(message, '*');
        // eslint-disable-next-line no-plusplus
      } else if (count++ < MAX_CHECK_IFRAME_EMBED_CHECKS) {
        /* in CN-43501, we pop the overlay after a timeout. The above
         * check ensures we are not polling indefinitely should Ordering
         * miss the overlay's `init` message. */
        setTimeout(() => {
          checkParentWindow();
        }, IFRAME_EMBED_RECHECK_INTERVAL);
      }
    }

    if (isDirectPlatform || isMarketplacePlatform) {
      checkParentWindow();
    }
  }

  useEffect(() => {
    handleFetchUserProfile();

    const messageProcessor = isYelpPlatform
      ? handleYelpMessage
      : checkIfCnEmbed;
    window.addEventListener('message', messageProcessor);

    // Note: react-route match isn't available yet to use for getting params
    const isEmbedded = window.top !== window;

    if (isEmbedded) {
      onEmbeddedAppLoad();
    }

    const isUnsupported =
      isIE() || (!getCanServeEmbeddedSite() && !isYelpPlatform);
    if (isUnsupported) return;

    // initiate user session ID (used by sift science)
    setSessionId(uuidv4());

    if (isLocalStorageAllowed()) {
      // Clear old local storage no longer used if exists
      const persistKeysToRemove = ['customer', 'menu', 'platform', 'root'];
      persistKeysToRemove.forEach((key) =>
        localStorage.removeItem(`directPersist:${key}`)
      );
    }

    async function checkApplePay() {
      if (!isEmbedded) {
        const hasActiveAppleCard = await hasApplePayCard();
        setHasActiveApplePayCard(hasActiveAppleCard);
        setIsApplePayLoading(false);
      } else {
        setIsApplePayLoading(false);
      }
    }

    if (isDirectPlatform || isMarketplacePlatform) {
      checkApplePay();
    }
  }, []);

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const applePayContextValue = {
    hasActiveApplePayCard,
    isApplePayLoading,
  };

  function checkBrowser(app) {
    const isUnsupported =
      isIE() || (!getCanServeEmbeddedSite() && !isYelpPlatform);
    const isEmbedded = window.top !== window;

    if (isUnsupported) {
      return (
        <BrowserUnsupportedPage
          isUnableToLoadSite={!getCanServeEmbeddedSite()}
          isEmbedded={isEmbedded}
        />
      );
    }

    return app;
  }

  return (
    <ErrorBoundary>
      <div
        className={cx({
          [styles.yelpOverflow]: isYelpPlatform && isYelpMobile(),
        })}
      >
        <SkipLink />
        {checkBrowser(
          <AppRoutes applePayContextProps={applePayContextValue} />
        )}

        <div id="app-portal-container" />
      </div>
    </ErrorBoundary>
  );
}

export default App;
